import { useCallback, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { StateContext } from "../context/globalContext/context";
import { iGlobalContext } from "../context/globalContext/interface";

export const useNavigationUrl = () => {
  const state = useContext(StateContext);
  const navigate = useNavigate();
  const { utmParams }: iGlobalContext = state;

  const constructNavigationUrl = useCallback(
    (basePath: string): string => {
      // Fall back to current URL search params when context utmParams
      // haven't been set yet (e.g. first redirect before state update)
      const paramsToUse = utmParams || window.location.search.replace(/^\?/, "");
      if (paramsToUse) {
        const params = new URLSearchParams(paramsToUse);
        const queryString = params.toString();
        return queryString ? `${basePath}?${queryString}` : basePath;
      }

      return basePath;
    },
    [utmParams]
  );

  const navigateWithParams = useCallback(
    (path: string, options?: any) => {
      const urlWithParams = constructNavigationUrl(path);
      // Skip a redundant PUSH to the location we're already on. Kills the streaming
      // per-chunk pushState flood that trips the browser's 100-history-calls/10s
      // throttle (amplified by Clarity/fbevents/gtag patching the History API) on
      // mobile /chat. PATHNAME-only compare: utmParams are session-stable and
      // already in window.location.search, and no caller pushes the same pathname
      // with an intentionally different query — so this is deterministic against
      // the flood and never suppresses a legitimate nav. `replace`/`state` pass through.
      const targetPath = urlWithParams.split("?")[0];
      if (!options?.replace && !options?.state && targetPath === window.location.pathname) {
        return;
      }
      navigate(urlWithParams, options);
    },
    [navigate, constructNavigationUrl]
  );

  const navigateBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Memoize the returned object so consumers using these functions in effect
  // dependency arrays (e.g. BackButtonHandler) don't re-run on every render.
  return useMemo(
    () => ({ constructNavigationUrl, navigateWithParams, navigateBack }),
    [constructNavigationUrl, navigateWithParams, navigateBack]
  );
};
